/** @jsxImportSource theme-ui */
import React, { useState } from "react";
import { Link } from "gatsby";
import siteLogo from "../images/ben-wulf-logo.svg";

const Header = () => {
  const isBrowser = typeof window !== "undefined";
  const pathname = isBrowser ? window.location.pathname : "";
  const isPortfolioPage = pathname.startsWith("/portfolio");
  const [navOpen, setNavOpen] = useState(false);
  const [portfolioOpen, setPortfolioOpen] = useState(false);
  const [headshotsOpen, setHeadshotsOpen] = useState(false);

  const toggleNavBar = () => {
    const burger = document.querySelector(".navbar-burger");
    const menu = document.querySelector(".navbar-menu");
    if (navOpen) {
      menu.classList.remove("is-active");
      burger.classList.remove("is-active");
      setNavOpen(false);
      setPortfolioOpen(false);
      setHeadshotsOpen(false);
    } else {
      menu.classList.add("is-active");
      burger.classList.add("is-active");
      setNavOpen(true);
    }
  };

  return (
    <header
      sx={{
        display: "block",
        "@media screen and (min-width: 1024px)": {
          display: "flex",
          justifyContent: "space-between",
        },
      }}
    >
      {/* Logo + Burger */}
      <div sx={{ display: "flex", justifyContent: "space-between" }}>
        <Link to="/">
          <img
            src={siteLogo}
            alt="Ben Wulf Photography Logo"
            sx={{ height: ["100px", "140px"] }}
          />
        </Link>
        <button
          className="navbar-burger burger"
          aria-label="Toggle navigation"
          aria-expanded={navOpen}
          onClick={toggleNavBar}
          sx={{
            display: "flex",
            alignSelf: "center",
            bg: "transparent",
            border: "none",
            color: "muted",
            height: "3.25rem",
            width: "3.25rem",
            ml: "auto",
            position: "relative",
            span: {
              position: "absolute",
              left: "calc(50% - 12px)",
              width: "24px",
              height: "1.5px",
              bg: "muted",
              transition: "all 0.2s ease",
              "&:first-of-type": { top: "calc(50% - 8px)" },
              "&:nth-of-type(2)": { top: "calc(50% - 2px)" },
              "&:nth-of-type(3)": { top: "calc(50% + 4px)" },
            },
            "&:hover, &:focus": { bg: "rgba(0,0,0,0.05)" },
            "@media screen and (min-width: 1024px)": {
              display: "none",
            },
          }}
        >
          <span aria-hidden="true" />
          <span aria-hidden="true" />
          <span aria-hidden="true" />
        </button>
      </div>

      {/* Main nav */}
      <nav
        aria-label="Main menu"
        className={`navbar-menu${navOpen ? " is-active" : ""}`}
        sx={{
          display: "none",
          opacity: 0,
          transition: "opacity 0.3s ease",
          "@media screen and (min-width: 1024px)": {
            display: "flex",
            opacity: 1,
          },
          "ul:first-of-type": {
            display: "flex",
            alignItems: "center",
            //height: "140px",
            m: 0,
            li: {
              listStyle: "none",
              px: 3,
              height: "100%",
              display: "flex",
              alignItems: "center",
              "&:hover, &:focus-within": { bg: "backgroundMuted" },
              a: {
                textDecoration: "none",
                color: "rgba(0,0,0,0.5)",
                fontSize: "1.2rem",
                display: "block",
                py: 2,
                "&:hover, &:focus": { color: "rgba(0,0,0,0.75)" },
                "&.is-active": { color: "rgba(0,0,0,1)" },
              },
            },
          },
          "@media screen and (max-width: 1023px)": {
            "&.is-active": {
              display: "block",
              opacity: 1,
              "& > ul:first-of-type": {
                display: "block",
                height: "auto",
                pl: 0,
                li: { "& a.is-active": { pl: 2 } },
              },
            },
          },
        }}
      >
        <ul>
          {/* Actors Headshots dropdown */}
          <li
            sx={{
              position: "relative",

              // 1) default (mobile): toggle via state
              "& > ul.headshots-submenu": {
                display: headshotsOpen ? "block" : "none",
                bg: "white",
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                borderRadius: 4,
                position: "absolute",
                top: "100%",
                left: 0,
                zIndex: 10,
                p: 0,
                m: 0,
                listStyle: "none",
                minWidth: "180px",
                // smaller padding on submenu items:
                li: {
                  px: 2,
                  py: 1,
                },
              },

              // 2) desktop: hide by default, show on hover/focus
              "@media screen and (min-width: 1024px)": {
                "& > ul:first-of-type": {
                  display: "none",
                },
                "&:hover > ul:first-of-type, &:focus-within > ul:first-of-type":
                  {
                    display: "block",
                  },
              },
            }}
          >
            {/* unified trigger for both desktop & mobile */}
            <button
              onClick={() => setHeadshotsOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={headshotsOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: "1.2rem",
                color: isPortfolioPage ? "rgba(0,0,0,1)" : "rgba(0,0,0,0.5)",
                bg: "transparent",
                border: "none",
                cursor: "pointer",
                py: 2,
                pl: 0,
                "&:hover, &:focus": { color: "rgba(0,0,0,0.75)" },
              }}
            >
              Actors Headshots
              <span
                sx={{
                  ml: 1,
                  transform: portfolioOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                ▾
              </span>
            </button>

            {/* single submenu used in both contexts */}
            <ul
              role="menu"
              aria-label="Headshots submenu"
              className={`headshots-submenu open-${headshotsOpen}`}
              sx={{
                "@media screen and (max-width: 1023px)": {
                  "&.open-true": {
                    display: "block",
                  },
                  "&.open-false": {
                    display: "none",
                  },
                },
              }}
            >
              {[
                {
                  to: "/actors-headshots-studio-light",
                  label: "Studio Light",
                },
                {
                  to: "/actors-headshots-natural-light",
                  label: "Natural Light",
                },
              ].map(({ to, label }) => (
                <li key={to} role="none">
                  <Link
                    role="menuitem"
                    to={to}
                    sx={{
                      display: "block",
                      px: 2,
                      py: 1,
                      "&:hover": { bg: "backgroundMuted" },
                    }}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
          {/* Portfolio dropdown */}
          <li
            sx={{
              position: "relative",

              // 1) default (mobile): toggle via state
              "& > ul.portfolio-submenu": {
                display: portfolioOpen ? "block" : "none",
                bg: "white",
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                borderRadius: 4,
                position: "absolute",
                top: "100%",
                left: 0,
                zIndex: 10,
                p: 0,
                m: 0,
                listStyle: "none",
                minWidth: "180px",
                // smaller padding on submenu items:
                li: {
                  px: 2,
                  py: 1,
                },
              },

              // 2) desktop: hide by default, show on hover/focus
              "@media screen and (min-width: 1024px)": {
                "& > ul:first-of-type": {
                  display: "none",
                },
                "&:hover > ul:first-of-type, &:focus-within > ul:first-of-type":
                  {
                    display: "block",
                  },
              },
            }}
          >
            {/* unified trigger for both desktop & mobile */}
            <button
              onClick={() => setPortfolioOpen((o) => !o)}
              aria-haspopup="menu"
              aria-expanded={portfolioOpen}
              sx={{
                display: "flex",
                alignItems: "center",
                fontSize: "1.2rem",
                color: isPortfolioPage ? "rgba(0,0,0,1)" : "rgba(0,0,0,0.5)",
                bg: "transparent",
                border: "none",
                cursor: "pointer",
                py: 2,
                pl: 0,
                "&:hover, &:focus": { color: "rgba(0,0,0,0.75)" },
              }}
            >
              Portfolio
              <span
                sx={{
                  ml: 1,
                  transform: portfolioOpen ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 0.2s ease",
                }}
              >
                ▾
              </span>
            </button>

            {/* single submenu used in both contexts */}
            <ul
              role="menu"
              aria-label="Portfolio submenu"
              className={`portfolio-submenu open-${portfolioOpen}`}
              sx={{
                "@media screen and (max-width: 1023px)": {
                  "&.open-true": {
                    display: "block",
                  },
                  "&.open-false": {
                    display: "none",
                  },
                },
              }}
            >
              {[
                { to: "/portfolio-portraits", label: "Portraits" },
                {
                  to: "/portfolio-corporate",
                  label: "Corporate",
                },
                {
                  to: "/portfolio-dancers",
                  label: "Dancers",
                },
                {
                  to: "/portfolio-bw",
                  label: "Black & White",
                },
                {
                  to: "/portfolio-rehearsals",
                  label: "Rehearsals",
                },
              ].map(({ to, label }) => (
                <li key={to} role="none">
                  <Link
                    role="menuitem"
                    to={to}
                    sx={{
                      display: "block",
                      px: 2,
                      py: 1,
                      "&:hover": { bg: "backgroundMuted" },
                    }}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </li>
          {/* rest of your top-level nav */}

          <li>
            <Link to="/about" activeClassName="is-active">
              About
            </Link>
          </li>
          <li>
            <Link to="/faqs" activeClassName="is-active">
              FAQs
            </Link>
          </li>
          <li>
            <Link to="/blog" activeClassName="is-active">
              Blog
            </Link>
          </li>
          <li>
            <Link to="/contact" activeClassName="is-active">
              Contact
            </Link>
          </li>
        </ul>
      </nav>
    </header>
  );
};

export default Header;
